import {createApi} from '@reduxjs/toolkit/query/react';
import {IArtifact, IUser, PinTypes} from 'app/types/Shared';
import {baseQuery} from 'app/helpers/auth';

interface CreateJUserArtifactPayload {
  userId: number;
  key: string;
  type: PinTypes.Artifact | PinTypes.Training;
}

interface CreateJUserURLPayload {
  userId: number;
  url: string;
  key: string;
  type: PinTypes.Process;
}

interface JUserPin {
  id: number;
  user_id: number;
  object_id: string;
  object_type: string;
  object_url?: string;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  time_zone: string;
  last_login_dt: Date;
  created_dt: Date;
  premissions: {
    name: string;
  };
}

interface CreateUserPayload {
  email: string;
  first_name: string;
  last_name: string;
  time_zone: string;
  last_login_dt: number;
  permission: string;
}
interface UpdateUserPayload {
  id: number;
  first_name?: string;
  last_name?: string;
  time_zone?: string;
  last_login_dt?: number;
  release?: string;
}

interface ProcessArtifact {
  id: number;
  process_id: string;
  name: string;
  parent_process?: string;
  parent_process_id: string;
  owner_name?: string;
  owner_email?: string;
  operating_models_list: string;
  created_dt: string;
}

interface UserAPI {
  user: IUser;
  artifactPins: Array<IArtifact>;
  processPins: Array<ProcessArtifact>;
}

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery,
  tagTypes: ['userApi'],
  endpoints(builder) {
    return {
      getUserByEmail: builder.query<UserAPI, string>({
        query: (email) => {
          return {
            url: `user/${email}`,
            method: 'GET',
          };
        },
      }),
      addUserArtifactPin: builder.mutation<JUserPin, CreateJUserArtifactPayload>({
        query: (body) => {
          return {
            url: `user/${body.userId}/pin/artifact`,
            method: 'POST',
            body,
          };
        },
      }),
      addUserUrlPin: builder.mutation<JUserPin, CreateJUserURLPayload>({
        query: (body) => {
          return {
            url: `user/${body.userId}/pin/url`,
            method: 'POST',
            body,
          };
        },
      }),
      removeUserPin: builder.mutation<void, {pinId: number; userId: number; key: string}>({
        query: (body) => {
          return {
            url: `user/pin/${body.pinId}`,
            method: 'DELETE',
            body,
          };
        },
      }),
      createUser: builder.mutation<User, CreateUserPayload>({
        query: (body) => {
          return {
            url: `user`,
            method: 'POST',
            body,
          };
        },
      }),
      updateUser: builder.mutation<User, UpdateUserPayload>({
        query: (body) => {
          return {
            url: `user/${body.id}`,
            method: 'POST',
            body,
          };
        },
      }),
    };
  },
});

export const {
  useGetUserByEmailQuery,
  useAddUserArtifactPinMutation,
  useAddUserUrlPinMutation,
  useRemoveUserPinMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
} = userApi;
